import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { Modal } from '../components/Modal.tsx';
import { Field, Select, TextInput } from '../components/Field.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import type { AdminUserListItem } from '../lib/types.ts';

type Role = 'admin' | 'reviewer';

export function AdminUsers() {
  const qc = useQueryClient();
  const { user: currentUser } = useAdminAuth();
  const isAdmin = currentUser?.role === 'admin';

  const { data, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => adminApi.get<{ users: AdminUserListItem[] }>('/api/users'),
  });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('admin');
  const [inviteError, setInviteError] = useState<unknown>(null);

  const inviteMutation = useMutation({
    mutationFn: (body: { email: string; name: string; role: Role }) =>
      adminApi.post<{ user: AdminUserListItem }>('/api/users', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setInviteOpen(false);
      setInviteEmail('');
      setInviteName('');
      setInviteRole('admin');
      setInviteError(null);
    },
    onError: (err) => setInviteError(err),
  });

  const updateMutation = useMutation({
    mutationFn: (input: { id: string; patch: { role?: Role; name?: string } }) =>
      adminApi.patch(`/api/users/${input.id}`, input.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.delete(`/api/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  if (isLoading) return <CenteredSpinner />;
  const users = data?.users ?? [];

  function onInviteSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    inviteMutation.mutate({
      email: inviteEmail.trim(),
      name: inviteName.trim(),
      role: inviteRole,
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Users</h1>
          <p className="mt-1 text-sm text-slate-500">
            Manage admin and reviewer access to the portal.
          </p>
        </div>
        {isAdmin && (
          <Btn onClick={() => setInviteOpen(true)}>Invite user</Btn>
        )}
      </div>

      {users.length === 0 ? (
        <EmptyState title="No users yet" description="Invite your first admin to get started." />
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => {
                const isSelf = u.id === currentUser?.id;
                return (
                  <tr key={u.id}>
                    <td className="px-4 py-3 font-medium text-slate-900">{u.name}</td>
                    <td className="px-4 py-3 text-slate-700">{u.email}</td>
                    <td className="px-4 py-3">
                      {isAdmin && !isSelf ? (
                        <Select
                          value={u.role}
                          onChange={(e) =>
                            updateMutation.mutate({
                              id: u.id,
                              patch: { role: e.target.value as Role },
                            })
                          }
                        >
                          <option value="admin">admin</option>
                          <option value="reviewer">reviewer</option>
                        </Select>
                      ) : (
                        <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                          {u.role}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isAdmin && !isSelf ? (
                        <Btn
                          variant="danger"
                          size="sm"
                          onClick={() => {
                            if (
                              window.confirm(
                                `Remove ${u.email}? They will lose access immediately.`,
                              )
                            ) {
                              deleteMutation.mutate(u.id);
                            }
                          }}
                          loading={deleteMutation.isPending && deleteMutation.variables === u.id}
                        >
                          Remove
                        </Btn>
                      ) : (
                        <span className="text-xs text-slate-400">
                          {isSelf ? 'You' : 'Read-only'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title="Invite user"
        footer={
          <>
            <Btn variant="secondary" onClick={() => setInviteOpen(false)}>
              Cancel
            </Btn>
            <Btn
              type="submit"
              form="invite-form"
              loading={inviteMutation.isPending}
              disabled={!inviteEmail.trim() || !inviteName.trim()}
            >
              Send invitation
            </Btn>
          </>
        }
      >
        <form id="invite-form" className="space-y-4" onSubmit={onInviteSubmit}>
          <Field label="Email" required>
            <TextInput
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              required
            />
          </Field>
          <Field label="Name" required>
            <TextInput
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
              required
            />
          </Field>
          <Field label="Role">
            <Select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as Role)}>
              <option value="admin">admin</option>
              <option value="reviewer">reviewer</option>
            </Select>
          </Field>
          <p className="text-xs text-slate-500">
            An invitation email will be sent. The user sets their password from the link, then signs
            in with their email and password.
          </p>
          <ErrorMessage error={inviteError} />
        </form>
      </Modal>
    </div>
  );
}
